/**
 * RFC 7807-style API error response shapes.
 *
 * Mirrors the structured exceptions thrown by SubscriptionService /
 * CreditService on the backend (see PR2: error contract foundation).
 * The axios interceptor in services/api.ts narrows on `error_code` to
 * route 402 / 403 responses to the global UpgradePrompt modal.
 */

export interface InsufficientCreditsError {
  error_code: 'insufficient_credits';
  message: string;
  details: {
    required: number;
    available: number;
    feature: string;
    resets_at: string | null;
  };
}

export interface EntitlementRequiredError {
  error_code: 'entitlement_required';
  message: string;
  details: {
    entitlement: string;
    required_plan: string;
    feature: string;
  };
}

/**
 * Discriminated union — TypeScript narrows on `error_code`. New error
 * types extend cleanly by adding to this union.
 */
export type ApiErrorPayload =
  | InsufficientCreditsError
  | EntitlementRequiredError
  | { error_code: string; message: string; details?: Record<string, unknown> };

export function isInsufficientCreditsError(payload: unknown): payload is InsufficientCreditsError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { error_code?: string }).error_code === 'insufficient_credits'
  );
}

export function isEntitlementRequiredError(payload: unknown): payload is EntitlementRequiredError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { error_code?: string }).error_code === 'entitlement_required'
  );
}

/**
 * The server refuses a session join (circle or 1:1) until the user has accepted the
 * confidentiality agreement. Handled globally in services/api.ts, which parks the
 * request, shows the agreement, and replays it on acceptance.
 * @see docs/plans/CONFIDENTIALITY-LIABILITY.md §4.1
 */
export interface CircleAgreementRequiredError {
  code: 'circle_agreement_required';
  message: string;
  data?: { version?: number };
}

export function isCircleAgreementRequiredError(
  payload: unknown,
): payload is CircleAgreementRequiredError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { code?: string }).code === 'circle_agreement_required'
  );
}

/**
 * The server refuses a 1:1 join until a user who has not completed the intro has
 * accepted "How empathy calls work" (COHORT-COMMUNICATION.md X2). Handled globally
 * in services/api.ts exactly like the circle agreement: park, ask, replay.
 */
export interface EmpathyNormsRequiredError {
  code: 'empathy_norms_required';
  message: string;
  data?: { version?: number };
}

export function isEmpathyNormsRequiredError(
  payload: unknown,
): payload is EmpathyNormsRequiredError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { code?: string }).code === 'empathy_norms_required'
  );
}

/**
 * The account is banned. Sent by the password login, Sign in with Apple, the
 * Google-callback ban notice and EVERY authenticated route (EnsureNotBanned),
 * always as a 403 with this body. Mapped once in stores/account-ban.store.ts.
 */
export interface AccountBannedError {
  success: false;
  error: 'account_banned';
  message: string;
  /** Apple's manage-subscriptions page when an Apple agreement may still bill them. */
  manage_subscription_url: string | null;
}

export function isAccountBannedError(payload: unknown): payload is AccountBannedError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { error?: string }).error === 'account_banned' &&
    typeof (payload as { message?: unknown }).message === 'string'
  );
}
